import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { Button, Card, Chip, FormField, Input, Select } from '@/ui';
import { useSessionStore } from '@/auth/session.store';
import { maskEmail, revealOrMask } from '@/lib/admin-mask';
import {
  EMPTY_USER_FILTERS,
  USER_SORT_OPTIONS,
  fetchPlatformUsers,
  type UserListFilters,
} from './api';
import { adminKeys, filtersKey } from './query-keys';
import { ROLE_LABEL_KEYS, USER_TYPE_LABEL_KEYS } from './labels';
import { formatDate } from './format';
import { ListStatus, LoadMore } from './ListStatus';
import { flattenPages, usePagedQuery } from './use-paged-query';
import styles from './Admin.module.css';

/**
 * PLATFORM KULLANICILARI (`/yonetim`, bolgenin acilis ekrani).
 *
 * ============================ ADRES CUBUGU BOS =============================
 * Filtreler URL'e YAZILMAZ. Bu ekranda arama kutusuna yazilan sey neredeyse her
 * zaman bir E-POSTA ADRESI ya da bir ad; adres cubugu ise gecmise, yer imlerine,
 * paylasilan baglantiya ve referrer basligina tasinir. Baska ekranlarda
 * (talepler, teklifler) filtrelerin URL'de olmasi dogru — orada filtre kisisel
 * veri degil. Burada oyle.
 *
 * ============================== EYLEM YOK ==================================
 * Listede DEGISTIREN hicbir buton yok; pasiflestirme / rol degisimi / sifre
 * sifirlama kullanici DETAYINDA. Sebep: bu uc islem de o kisinin butun
 * oturumlarini dusuruyor ve satir listesinde yanlis satira tiklamak fazla kolay.
 * Detay sayfasi kimi etkiledigini tam olarak gosteriyor.
 *
 * ========================== ILETISIM MASKELI ===============================
 * E-posta varsayilan olarak maskeli. Yirmi kisinin adresi ekranda acik durmasin
 * diye; gormek bilincli bir hareket (anahtar) istiyor. Bu bir guvenlik onlemi
 * degil — veri zaten tarayicida — gereksiz BAKISI zorlastiran bir onlem.
 */

/** Aktiflik filtresi Select'te dize; ucte boolean. Donusum TEK yerde. */
const ACTIVE_VALUES = { all: '', active: 'true', inactive: 'false' } as const;

const toIsActive = (value: string): boolean | null => {
  if (value === ACTIVE_VALUES.active) return true;
  if (value === ACTIVE_VALUES.inactive) return false;
  return null;
};

/** Yonlendirme durumundan gelen isletme suzgeci (isletme detayindan geliniyorsa). */
const organizationIdFromState = (state: unknown): string | null => {
  if (typeof state !== 'object' || state === null) return null;
  const value = (state as { organizationId?: unknown }).organizationId;
  // Kimlik pg int8 — STRING. Number()'a cevrilmez.
  return typeof value === 'string' && value.length > 0 ? value : null;
};

export function PlatformUsersPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const selfUserId = useSessionStore((state) => state.snapshot?.user.id ?? '');

  // Isletme detayindan "uyeleri kullanici olarak gor" ile gelinmis olabilir.
  // Yol parametresi degil ROTA DURUMU kullaniliyor: kimlik adres cubuguna
  // yazilmasin diye.
  const initialOrganizationId = useMemo(
    () => organizationIdFromState(location.state),
    [location.state],
  );

  /** Kutulara yazilan; henuz istege donusmemis hali. */
  const [draft, setDraft] = useState<UserListFilters>({
    ...EMPTY_USER_FILTERS,
    organizationId: initialOrganizationId,
  });
  /** Sunucuya gonderilen hali. Ayri tutuluyor: her tusa basista istek atilmasin. */
  const [applied, setApplied] = useState<UserListFilters>({
    ...EMPTY_USER_FILTERS,
    organizationId: initialOrganizationId,
  });
  const [revealContact, setRevealContact] = useState(false);

  const key = filtersKey({
    q: applied.q.trim(),
    userType: applied.userType,
    isActive: applied.isActive,
    organizationId: applied.organizationId,
    sort: applied.sort,
  });

  const query = usePagedQuery(adminKeys.userList(key), (pageParam) =>
    fetchPlatformUsers({ filters: applied, ...pageParam }),
  );

  const rows = flattenPages(query.data);
  const filtered =
    applied.q.trim() !== '' ||
    applied.userType !== null ||
    applied.isActive !== null ||
    applied.organizationId !== null;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setApplied(draft);
  };

  const clear = (): void => {
    setDraft(EMPTY_USER_FILTERS);
    setApplied(EMPTY_USER_FILTERS);
  };

  return (
    <div className={styles.page}>
      <Card padding="md">
        <div className={styles.pageHead}>
          <div>
            <h1 className={styles.title}>{t('admin.users.title')}</h1>
            {/* `total` YOK — gosterilen sayi YUKLENEN satirlarin sayisi. */}
            <p className={styles.subtitle}>
              {t('admin.users.loadedCount', { n: rows.length })}
            </p>
          </div>
        </div>
      </Card>

      <Card padding="md">
        <form className={styles.filterRow} onSubmit={submit}>
          <FormField
            label={t('admin.users.searchLabel')}
            hint={t('admin.users.searchHint')}
            className={styles.filterField}
          >
            <Input
              value={draft.q}
              placeholder={t('filter.search')}
              autoComplete="off"
              onChange={(event) => {
                setDraft((current) => ({ ...current, q: event.target.value }));
              }}
            />
          </FormField>

          <FormField label={t('admin.users.typeLabel')} className={styles.filterField}>
            <Select
              value={draft.userType ?? ''}
              placeholder={t('admin.filters.all')}
              options={[
                { value: 'customer', label: t(USER_TYPE_LABEL_KEYS.customer ?? '') },
                { value: 'exchange', label: t(USER_TYPE_LABEL_KEYS.exchange ?? '') },
              ]}
              onChange={(event) => {
                const value = event.target.value;
                setDraft((current) => ({
                  ...current,
                  userType: value === 'customer' || value === 'exchange' ? value : null,
                }));
              }}
            />
          </FormField>

          <FormField label={t('admin.users.statusLabel')} className={styles.filterField}>
            <Select
              value={
                draft.isActive === null ? '' : draft.isActive ? ACTIVE_VALUES.active : ACTIVE_VALUES.inactive
              }
              placeholder={t('admin.filters.all')}
              options={[
                { value: ACTIVE_VALUES.active, label: t('admin.status.active') },
                { value: ACTIVE_VALUES.inactive, label: t('admin.status.inactive') },
              ]}
              onChange={(event) => {
                const next = toIsActive(event.target.value);
                setDraft((current) => ({ ...current, isActive: next }));
              }}
            />
          </FormField>

          <FormField label={t('filter.sort')} className={styles.filterField}>
            <Select
              value={draft.sort}
              options={USER_SORT_OPTIONS.map((option) => ({
                value: option.value,
                // Sabit HARITA anahtar tutar; ceviri BURADA, render sirasinda.
                label: t(option.labelKey),
              }))}
              onChange={(event) => {
                setDraft((current) => ({ ...current, sort: event.target.value }));
              }}
            />
          </FormField>

          <div className={styles.filterActions}>
            <Button type="submit">{t('filter.apply')}</Button>
            <Button type="button" tone="ghost" onClick={clear}>
              {t('filter.clearAll')}
            </Button>
          </div>
        </form>

        <label className={styles.revealToggle}>
          <input
            type="checkbox"
            checked={revealContact}
            onChange={(event) => {
              setRevealContact(event.target.checked);
            }}
          />
          {t('admin.revealContact')}
        </label>
      </Card>

      {applied.organizationId === null ? null : (
        <Card padding="sm">
          <div className={styles.filterActions}>
            <Chip tone="info">{t('admin.users.filteredByOrganization')}</Chip>
            <Button tone="ghost" size="sm" onClick={clear}>
              {t('filter.clearAll')}
            </Button>
          </div>
        </Card>
      )}

      <ListStatus
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        isEmpty={query.isSuccess && rows.length === 0}
        emptyTitle={filtered ? t('list.noResults.title') : t('list.empty.title')}
        {...(filtered ? { emptyDescription: t('list.noResults.description') } : {})}
        onRetry={() => {
          void query.refetch();
        }}
      />

      {rows.length > 0 ? (
        <Card padding="lg">
          <div className={styles.rowList}>
            {rows.map((row) => {
              // pg int8 STRING doner — String()/String() karsilastirmasi ZORUNLU.
              const isSelf = String(row.id) === String(selfUserId);
              const typeKey =
                row.userType === null ? null : (USER_TYPE_LABEL_KEYS[row.userType] ?? null);
              return (
                <div key={row.id} className={styles.row}>
                  <div className={styles.rowMain}>
                    <span className={styles.rowName}>
                      {row.userFullName || t('admin.users.unnamed')}
                    </span>
                    <span className={styles.muted}>
                      {revealOrMask(row.userEmail, revealContact, maskEmail)}
                    </span>
                    <span className={styles.muted}>
                      {row.organizationName ?? t('admin.users.noOrganization')} ·{' '}
                      {t('admin.users.joined', { date: formatDate(row.createdAt) })}
                    </span>
                  </div>
                  <div className={styles.badges}>
                    {isSelf ? <Chip tone="info">{t('members.you')}</Chip> : null}
                    {typeKey === null ? null : <Chip tone="neutral">{t(typeKey)}</Chip>}
                    {row.roleCode === 'super_admin' ? (
                      <Chip tone="warning">{t(ROLE_LABEL_KEYS.super_admin ?? '')}</Chip>
                    ) : null}
                    <Chip tone={row.isActive ? 'success' : 'danger'}>
                      {row.isActive ? t('admin.status.active') : t('admin.status.inactive')}
                    </Chip>
                    <Link to={`/yonetim/kullanicilar/${row.id}`} className={styles.linkButton}>
                      {t('admin.users.openDetail')}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          <LoadMore
            hasNextPage={query.hasNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            onLoadMore={() => {
              void query.fetchNextPage();
            }}
          />
        </Card>
      ) : null}
    </div>
  );
}
